/**
 * Inline SVG icon set — stroke-based, inherits `currentColor`, sized via
 * the `size` prop (default 18). Crisp at any DPI and theme-aware, unlike
 * emoji glyphs. Used across the Dashboard's icon-buttons (ADR 0012 UI
 * polish) and elsewhere.
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({
  size = 18,
  children,
  ...rest
}: IconProps & { children: React.ReactNode }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.85}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const PlusIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const PencilIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M4 20h4L18.5 9.5a2.12 2.12 0 0 0-3-3L5 17v3Z" />
    <path d="M13.5 6.5l4 4" />
  </Svg>
);

export const TrashIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M4 7h16" />
    <path d="M9 7V5h6v2" />
    <path d="M6 7l1 13h10l1-13" />
    <path d="M10 11v6M14 11v6" />
  </Svg>
);

export const ChevronRightIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M9 5l7 7-7 7" />
  </Svg>
);

export const CheckIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M20 6L9 17l-5-5" />
  </Svg>
);

export const XIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);

export const InboxIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M3 12h5l1.5 2.5h5L16 12h5" />
    <path d="M5.5 6h13l2.5 6v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z" />
  </Svg>
);

export const AlertIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M10.3 4 2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 4a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </Svg>
);

export const ChecklistIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M10 6h11M10 12h11M10 18h11" />
    <path d="M3 6l1.5 1.5L7 5" />
    <path d="M3 12l1.5 1.5L7 11" />
    <path d="M3 18l1.5 1.5L7 17" />
  </Svg>
);

export const FolderIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M3 7a2 2 0 0 1 2-2h3.5l2 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
  </Svg>
);

export const SparkleIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16l-1.8-4.5L5 9.7l5.2-1.8L12 3Z" />
  </Svg>
);

export const NoteIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M14 3v4h4" />
    <path d="M8.5 12h7M8.5 16h7" />
  </Svg>
);

export const CheckCircleIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </Svg>
);

export const FileIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M14 3v4h4" />
  </Svg>
);

export const UsersIcon = (p: IconProps): React.ReactElement => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.2a3.2 3.2 0 0 1 0 6.1" />
    <path d="M17.5 13.4a5.5 5.5 0 0 1 3 5.1" />
  </Svg>
);

export const StarIcon = ({
  size = 18,
  filled = false,
  ...rest
}: IconProps & { filled?: boolean }): React.ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={1.85}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    <path d="M12 3.6l2.6 5.28 5.82.85-4.21 4.1.99 5.79L12 16.9l-5.2 2.72.99-5.79L3.58 9.73l5.82-.85L12 3.6Z" />
  </svg>
);
